import {
  type Bbox,
  type Feature,
  GeomType,
  type JsonObject,
  type Label,
  type LabelSymbolizer,
  type Layout,
} from "protomaps-leaflet";
import type Point from "@mapbox/point-geometry";

const LABEL_FONT_STYLE = "normal 12px sans-serif";
const HALO_WIDTH = 3; // 白フチの太さ(px)
const TEXT_COLOR = "#333333";
const HALO_COLOR = "#ffffff";
const DEDUP_DISTANCE = 8; // ラベル同士の最小間隔(px)

/**
 * 筆の代表点（Point）に地番ラベルを描画する LabelSymbolizer。
 *
 * 地番ラベルはポリゴン中心ではなく、タイルに焼き込んだ代表点（parcel_labels
 * レイヤーの Point）に付ける。Point はタイル境界で分割されないため、境界をまたぐ
 * 筆でもラベルが2枚のタイルに二重描画されない。代表点は筆内に必ず収まる
 * representative_point なので、L字など歪な筆でもラベルが筆外に出ない。
 * 地図背景上でも視認できるよう白フチ（ハロー）を付け、中央寄せで描画する。
 */
export class ParcelLabelSymbolizer implements LabelSymbolizer {
  private readonly textGetter: (props: JsonObject) => string;

  constructor(options: { textGetter: (props: JsonObject) => string }) {
    this.textGetter = options.textGetter;
  }

  place(
    layout: Layout,
    geom: Point[][],
    feature: Feature
  ): Label[] | undefined {
    if (feature.geomType !== GeomType.Point) return undefined;

    const anchor = geom[0]?.[0];
    if (anchor === undefined) return undefined;

    const name = this.textGetter(feature.props);
    if (name === "") return undefined;

    const font = LABEL_FONT_STYLE;
    layout.scratch.font = font;
    const metrics = layout.scratch.measureText(name);
    const halfWidth = metrics.width / 2 + HALO_WIDTH;
    const halfHeight =
      (metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent) / 2 +
      HALO_WIDTH;

    const bbox: Bbox = {
      minX: anchor.x - halfWidth,
      minY: anchor.y - halfHeight,
      maxX: anchor.x + halfWidth,
      maxY: anchor.y + halfHeight,
    };

    const draw = (ctx: CanvasRenderingContext2D): void => {
      ctx.font = font;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.lineWidth = HALO_WIDTH;
      ctx.strokeStyle = HALO_COLOR;
      ctx.strokeText(name, 0, 0);
      ctx.fillStyle = TEXT_COLOR;
      ctx.fillText(name, 0, 0);
    };

    return [
      {
        anchor,
        bboxes: [bbox],
        draw,
        deduplicationDistance: DEDUP_DISTANCE,
      },
    ];
  }
}
